/**
 * Role Profile dialog: view, edit, submit, approve and print one role's profile.
 *
 * The profile is the versioned, approved description of a Role Master role:
 * purpose, accountability areas and deliverables (each with KPIs), the fixed
 * 4Sight managerial / leadership items, stakeholders and requirements. Live
 * facts (function, reporting lines, headcount, location) come from Role Master
 * and the positions and are shown here but never stored in the profile.
 *
 * Approving publishes a draft as the live version: the one printed profiles,
 * Neptune requisitions and the performance module use. Only holders of the
 * role_profile:approve grant (and admins) can approve; the server enforces it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle, CheckCircle2, Copy, Download, FileText, History, Loader2, Pencil, Plus,
  Send, ShieldCheck, Trash2, X,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import roleProfileService, {
  type FrameworkItemDef, type ProfileContent, type ProfileFramework, type ProfileItem,
  type ProfileSummaryRow, type ProfileVersion, type RoleProfileBundle,
} from '@/services/api/roleProfileService';

// -- shared helpers --------------------------------------------------------------------------

let frameworkPromise: Promise<ProfileFramework> | null = null;
export function loadProfileFramework(): Promise<ProfileFramework> {
  if (!frameworkPromise) {
    frameworkPromise = roleProfileService.getFramework().catch((e) => {
      frameworkPromise = null;
      throw e;
    });
  }
  return frameworkPromise;
}

export function apiError(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function profileFilename(title: string, version: number, draft: boolean) {
  const safe = title.replace(/[^A-Za-z0-9 ._-]+/g, '').trim() || 'role';
  return `Role Profile - ${safe} v${version}${draft ? ' DRAFT' : ''}.docx`;
}

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-800' },
  submitted: { label: 'Awaiting approval', cls: 'bg-blue-100 text-blue-800' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  superseded: { label: 'Superseded', cls: 'bg-gray-100 text-gray-600' },
};
const SOURCE_LABEL: Record<string, string> = {
  manual: 'typed in', word: 'Word upload', excel: 'Excel upload', copy: 'copied from another role',
};

// -- form model ------------------------------------------------------------------------------

type FwState = 'filled' | 'na' | 'blank';
interface FwRow { state: FwState; detail: string; kpi: string }
interface OutRow { detail: string; kpi: string }
interface FormState {
  purpose: string;
  advert_summary: string;
  work_conditions: string;
  accountability: OutRow[];
  deliverables: OutRow[];
  framework: Record<string, FwRow>;
  qualifications_minimum: string;
  qualifications_advantageous: string;
  experience_minimum: string;
  experience_advantageous: string;
  min_education_nqf: string;
  functional_competencies: string;
  behavioural_competencies: string;
  reviewed_at: string;
}

const lines = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean);

function toForm(c: ProfileContent, fw: ProfileFramework): FormState {
  const byCode = new Map(c.items.filter((i) => i.kind === 'framework').map((i) => [i.framework_code, i]));
  const framework: Record<string, FwRow> = {};
  fw.framework_items.forEach((f) => {
    const it = byCode.get(f.code);
    framework[f.code] = {
      state: !it ? 'blank' : it.applicable === false ? 'na' : it.detail ? 'filled' : 'blank',
      detail: it?.detail ?? '',
      kpi: it?.kpi ?? '',
    };
  });
  const out = (kind: 'accountability' | 'deliverable') =>
    c.items.filter((i) => i.kind === kind).map((i) => ({ detail: i.detail ?? '', kpi: i.kpi ?? '' }));
  return {
    purpose: c.purpose ?? '',
    advert_summary: c.advert_summary ?? '',
    work_conditions: c.work_conditions ?? '',
    accountability: out('accountability'),
    deliverables: out('deliverable'),
    framework,
    qualifications_minimum: c.qualifications_minimum.join('\n'),
    qualifications_advantageous: c.qualifications_advantageous.join('\n'),
    experience_minimum: c.experience_minimum.join('\n'),
    experience_advantageous: c.experience_advantageous.join('\n'),
    min_education_nqf: c.min_education_nqf ? String(c.min_education_nqf) : '',
    functional_competencies: c.functional_competencies.join('\n'),
    behavioural_competencies: c.behavioural_competencies.join('\n'),
    reviewed_at: c.reviewed_at ?? '',
  };
}

function fromForm(f: FormState, fw: ProfileFramework): ProfileContent {
  const items: ProfileItem[] = [];
  const free = (kind: 'accountability' | 'deliverable', rows: OutRow[]) =>
    rows.filter((r) => r.detail.trim()).forEach((r) => items.push({
      perspective: 'operating-financial', kind, framework_code: null, title: null,
      detail: r.detail.trim(), kpi: r.kpi.trim() || null, applicable: true,
    }));
  free('accountability', f.accountability);
  free('deliverable', f.deliverables);
  fw.framework_items.forEach((def) => {
    const r = f.framework[def.code];
    if (!r) return;
    if (r.state === 'na') {
      items.push({ perspective: def.perspective, kind: 'framework', framework_code: def.code, title: def.title,
        detail: null, kpi: r.kpi.trim() || null, applicable: false });
    } else if (r.detail.trim() || r.kpi.trim()) {
      items.push({ perspective: def.perspective, kind: 'framework', framework_code: def.code, title: def.title,
        detail: r.detail.trim() || null, kpi: r.kpi.trim() || null, applicable: true });
    }
  });
  return {
    purpose: f.purpose.trim() || null,
    advert_summary: f.advert_summary.trim() || null,
    work_conditions: f.work_conditions.trim() || null,
    qualifications_minimum: lines(f.qualifications_minimum),
    qualifications_advantageous: lines(f.qualifications_advantageous),
    experience_minimum: lines(f.experience_minimum),
    experience_advantageous: lines(f.experience_advantageous),
    min_education_nqf: f.min_education_nqf ? Number(f.min_education_nqf) : null,
    functional_competencies: lines(f.functional_competencies),
    behavioural_competencies: lines(f.behavioural_competencies),
    reviewed_at: f.reviewed_at || null,
    items,
  };
}

// -- small presentational pieces ---------------------------------------------------------------

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <section className="rounded-lg border">
    <header className="rounded-t-lg bg-heritage-blue px-3 py-2 text-white">
      <h3 className="text-sm font-semibold">{title}</h3>
      {subtitle && <p className="text-xs text-white/80">{subtitle}</p>}
    </header>
    <div className="space-y-3 p-3">{children}</div>
  </section>
);

const Bullets: React.FC<{ items: string[]; empty?: string }> = ({ items, empty = 'None recorded' }) =>
  items.length ? (
    <ul className="list-disc space-y-0.5 pl-5 text-sm">{items.map((t, i) => <li key={i} className="whitespace-pre-wrap">{t}</li>)}</ul>
  ) : <p className="text-sm italic text-gray-400">{empty}</p>;

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
    <div className="truncate text-sm text-gray-800" title={typeof value === 'string' ? value : undefined}>{value || '—'}</div>
  </div>
);

const pairs = (p: [string, number][]) => p.map(([n, c]) => (c > 1 ? `${n} (${c})` : n)).join(', ');

// -- read-only view ------------------------------------------------------------------------------

const ProfileView: React.FC<{ version: ProfileVersion; fw: ProfileFramework }> = ({ version, fw }) => {
  const c = version.content;
  const acc = c.items.filter((i) => i.kind === 'accountability');
  const dlv = c.items.filter((i) => i.kind === 'deliverable');
  const byCode = new Map(c.items.filter((i) => i.kind === 'framework').map((i) => [i.framework_code, i]));
  const nqf = fw.nqf_levels.find((n) => n.level === c.min_education_nqf)?.label;
  const kindLabel = (k: string) => fw.kinds.find((x) => x.code === k)?.label ?? k;
  const outputs = (rows: ProfileItem[], kind: string) => (
    <div>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-heritage-blue">{kindLabel(kind)}</h4>
      {rows.length ? (
        <table className="w-full table-fixed text-sm">
          <thead><tr className="text-left text-xs text-gray-500"><th className="w-3/5 pb-1">Output</th><th className="pb-1">KPI</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i} className="border-t align-top"><td className="whitespace-pre-wrap py-1 pr-3">{r.detail}</td><td className="whitespace-pre-wrap py-1 text-gray-600">{r.kpi || '—'}</td></tr>
          ))}</tbody>
        </table>
      ) : <p className="text-sm italic text-gray-400">Not applicable to this role</p>}
    </div>
  );
  const frameworkBlock = (perspective: string) => (
    <table className="w-full table-fixed text-sm">
      <tbody>{fw.framework_items.filter((f) => f.perspective === perspective).map((f) => {
        const it = byCode.get(f.code);
        return (
          <tr key={f.code} className="border-t align-top">
            <td className="w-2/5 py-1 pr-3 text-gray-600">{f.title}</td>
            <td className="py-1">
              {it?.applicable === false ? <span className="italic text-gray-400">Not applicable</span>
                : it?.detail ? <span className="whitespace-pre-wrap">{it.detail}</span>
                : <span className="italic text-gray-300">Not filled in</span>}
              {it?.kpi && <div className="mt-0.5 text-xs text-gray-500">KPI: {it.kpi}</div>}
            </td>
          </tr>
        );
      })}</tbody>
    </table>
  );
  return (
    <div className="space-y-4">
      <Section title="Purpose of the role">
        <p className="whitespace-pre-wrap text-sm">{c.purpose || <span className="italic text-gray-400">Not filled in</span>}</p>
        {c.advert_summary && (<div><Label className="text-xs text-gray-500">Advert summary</Label><p className="whitespace-pre-wrap text-sm">{c.advert_summary}</p></div>)}
      </Section>
      <Section title="Operating and financial results" subtitle="Outputs and measurement criteria">
        {outputs(acc, 'accountability')}
        {outputs(dlv, 'deliverable')}
      </Section>
      <Section title="Internal business processes and enablement" subtitle="Managerial work">{frameworkBlock('internal-business-processes')}</Section>
      <Section title="Culture and people" subtitle="Leadership work">{frameworkBlock('culture-people')}</Section>
      <Section title="Customer and stakeholder relationships">{frameworkBlock('customer-stakeholder')}</Section>
      <Section title="Role requirements" subtitle="Enablers of effective performance in this role">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-heritage-blue">Qualifications</h4>
            <p className="mb-1 text-xs text-gray-500">Minimum education level: {nqf ?? 'not set'}</p>
            <Label className="text-xs text-gray-500">Minimum</Label><Bullets items={c.qualifications_minimum} />
            <Label className="text-xs text-gray-500">Advantageous</Label><Bullets items={c.qualifications_advantageous} empty="None" />
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-heritage-blue">Experience</h4>
            <Label className="text-xs text-gray-500">Minimum</Label><Bullets items={c.experience_minimum} />
            <Label className="text-xs text-gray-500">Advantageous</Label><Bullets items={c.experience_advantageous} empty="None" />
          </div>
          <div><h4 className="text-xs font-semibold uppercase tracking-wide text-heritage-blue">Functional competencies</h4><Bullets items={c.functional_competencies} /></div>
          <div><h4 className="text-xs font-semibold uppercase tracking-wide text-heritage-blue">Behavioural competencies</h4><Bullets items={c.behavioural_competencies} /></div>
          <div className="md:col-span-2"><h4 className="text-xs font-semibold uppercase tracking-wide text-heritage-blue">Unique work conditions</h4><p className="text-sm">{c.work_conditions || '—'}</p></div>
        </div>
      </Section>
    </div>
  );
};

// -- editor ----------------------------------------------------------------------------------------

const OutRowsEditor: React.FC<{
  label: string; help: string; rows: OutRow[]; onChange: (rows: OutRow[]) => void;
}> = ({ label, help, rows, onChange }) => (
  <div>
    <div className="mb-1 flex items-baseline justify-between gap-2">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-heritage-blue">{label}</h4>
        <p className="text-xs text-gray-500">{help}</p>
      </div>
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...rows, { detail: '', kpi: '' }])}>
        <Plus className="mr-1 h-3.5 w-3.5" /> Add
      </Button>
    </div>
    {rows.length === 0 && <p className="text-sm italic text-gray-400">None. Add one if this applies to the role.</p>}
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)_auto] gap-2">
          <Textarea rows={2} value={r.detail} placeholder="What the role must provide or deliver"
            onChange={(e) => onChange(rows.map((x, k) => (k === i ? { ...x, detail: e.target.value } : x)))} />
          <Textarea rows={2} value={r.kpi} placeholder="KPI: how a superior output is measured"
            onChange={(e) => onChange(rows.map((x, k) => (k === i ? { ...x, kpi: e.target.value } : x)))} />
          <Button type="button" size="icon" variant="ghost" aria-label="Remove row"
            onClick={() => onChange(rows.filter((_, k) => k !== i))}><X className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  </div>
);

const FrameworkEditor: React.FC<{
  defs: FrameworkItemDef[]; rows: Record<string, FwRow>; onChange: (code: string, row: FwRow) => void;
}> = ({ defs, rows, onChange }) => (
  <div className="space-y-2">
    {defs.map((d) => {
      const r = rows[d.code] ?? { state: 'blank' as FwState, detail: '', kpi: '' };
      return (
        <div key={d.code} className="rounded border p-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-800">{d.title}</span>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input type="checkbox" checked={r.state === 'na'}
                onChange={(e) => onChange(d.code, { ...r, state: e.target.checked ? 'na' : (r.detail ? 'filled' : 'blank') })} />
              Not applicable to this role
            </label>
          </div>
          {r.state !== 'na' && (
            <div className="mt-1.5 grid gap-2 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <Textarea rows={2} value={r.detail} placeholder="How this role contributes"
                onChange={(e) => onChange(d.code, { ...r, detail: e.target.value, state: e.target.value.trim() ? 'filled' : 'blank' })} />
              <Textarea rows={2} value={r.kpi} placeholder={d.default_kpi ? `KPI (default: ${d.default_kpi.replace(/\n/g, '; ')})` : 'KPI (optional)'}
                onChange={(e) => onChange(d.code, { ...r, kpi: e.target.value })} />
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const ListBox: React.FC<{ label: string; hint?: string; value: string; onChange: (v: string) => void; rows?: number }> =
  ({ label, hint, value, onChange, rows = 3 }) => (
    <div>
      <Label className="text-xs font-semibold text-gray-700">{label}</Label>
      {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
      <Textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} placeholder="One per line" />
    </div>
  );

// -- the dialog ------------------------------------------------------------------------------------

interface Props {
  roleId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after any change that alters the role's profile state. */
  onChanged?: () => void;
  /** Roles to offer for "copy from another role" (from the Profiles summary). */
  copySources?: ProfileSummaryRow[];
}

export const RoleProfileDialog: React.FC<Props> = ({ roleId, open, onOpenChange, onChanged, copySources }) => {
  const [fw, setFw] = useState<ProfileFramework | null>(null);
  const [bundle, setBundle] = useState<RoleProfileBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [viewing, setViewing] = useState<'approved' | 'open'>('approved');
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');
  const [tab, setTab] = useState('profile');

  const load = useCallback(async () => {
    if (!roleId) return;
    setLoading(true);
    try {
      const [f, b] = await Promise.all([loadProfileFramework(), roleProfileService.getRole(roleId)]);
      setFw(f);
      setBundle(b);
      setViewing(b.open ? 'open' : 'approved');
    } catch (e) {
      toast.error(apiError(e, 'Could not load the role profile.'));
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    if (open && roleId) {
      setEditing(false); setForm(null); setDirty(false); setApproveOpen(false); setCopyOpen(false); setTab('profile');
      load();
    }
  }, [open, roleId, load]);

  const shown: ProfileVersion | null = useMemo(() => {
    if (!bundle) return null;
    return viewing === 'open' ? (bundle.open ?? bundle.approved) : (bundle.approved ?? bundle.open);
  }, [bundle, viewing]);

  const act = async (label: string, fn: () => Promise<RoleProfileBundle>, success: string) => {
    setBusy(label);
    try {
      const b = await fn();
      setBundle(b);
      toast.success(success);
      onChanged?.();
      return b;
    } catch (e) {
      toast.error(apiError(e, 'That did not work. Try again.'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const startEditing = async () => {
    if (!roleId || !fw) return;
    let b = bundle;
    if (!b?.open) {
      b = await act('start', () => roleProfileService.startDraft(roleId, 'approved'), 'Draft started');
      if (!b) return;
    }
    setForm(toForm(b!.open!.content, fw));
    setViewing('open');
    setEditing(true);
    setDirty(false);
  };

  const save = async (): Promise<boolean> => {
    if (!roleId || !fw || !form) return false;
    const b = await act('save', () => roleProfileService.saveDraft(roleId, fromForm(form, fw)), 'Draft saved');
    if (b) { setDirty(false); return true; }
    return false;
  };

  const stopEditing = async () => {
    if (dirty && !(await save())) return;
    setEditing(false);
    setForm(null);
  };

  const download = async (version?: number) => {
    if (!roleId || !bundle) return;
    const v = version ? bundle.history.find((h) => h.version === version) : (bundle.approved ?? bundle.open);
    if (!v) return;
    setBusy('download');
    try {
      const blob = await roleProfileService.exportDocx(roleId, version);
      downloadBlob(blob, profileFilename(bundle.role.title, v.version, v.status !== 'approved'));
    } catch (e) {
      toast.error(apiError(e, 'Could not build the Word document.'));
    } finally {
      setBusy(null);
    }
  };

  const setF = (patch: Partial<FormState>) => { setForm((f) => (f ? { ...f, ...patch } : f)); setDirty(true); };

  const role = bundle?.role;
  const openV = bundle?.open ?? null;
  const approvedV = bundle?.approved ?? null;
  const completeness = (editing && openV) ? openV.completeness : shown?.completeness;
  const sources = (copySources ?? []).filter((r) => r.role_id !== roleId && (r.approved_version || r.open_version));

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o && dirty && !window.confirm('Close without saving your changes to this draft?')) return;
      if (!o) { setDirty(false); setEditing(false); setForm(null); }
      onOpenChange(o);
    }}>
      <DialogContent className="max-w-6xl max-h-[92vh]">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <FileText className="h-5 w-5 text-heritage-blue" />
            <span>{role?.title ?? 'Role profile'}</span>
            {role && <span className="font-mono text-sm text-gray-500">{role.role_code}</span>}
            {approvedV && <Badge className={STATUS_BADGE.approved.cls}>Approved v{approvedV.version}</Badge>}
            {openV && <Badge className={STATUS_BADGE[openV.status].cls}>{STATUS_BADGE[openV.status].label} v{openV.version}</Badge>}
            {bundle && !approvedV && !openV && <Badge className="bg-gray-100 text-gray-600">No profile yet</Badge>}
          </DialogTitle>
          <DialogDescription>
            {approvedV
              ? `Live version approved ${fmtDate(approvedV.approved_at)}${approvedV.approved_by_name ? ` by ${approvedV.approved_by_name}` : ''}. Printed profiles and Neptune requisitions use this version.`
              : 'No approved version yet: nothing feeds Neptune or printed profiles until one is approved.'}
          </DialogDescription>
        </DialogHeader>

        {loading || !bundle || !fw ? (
          <div className="flex items-center justify-center py-16 text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading profile…</div>
        ) : (
          <div className="space-y-4">
            {/* live facts */}
            <div className="rounded-lg border bg-gray-50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Live from Role Master</span>
                <span className="text-[11px] text-gray-400">Shown and printed with the profile, never stored in it</span>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Fact label="Function / department" value={[role!.function, role!.department, role!.team_1].filter(Boolean).join(' / ')} />
                <Fact label="Grade" value={role!.grade} />
                <Fact label="Seats" value={`${role!.seats} (${role!.employees_in_role} filled, ${role!.vacant_seats} vacant)`} />
                <Fact label="Direct reports" value={String(role!.direct_reports)} />
                <Fact label="Reports to" value={pairs(role!.reports_to_roles)} />
                <Fact label="Roles reporting in" value={pairs(role!.roles_reporting_in)} />
                <Fact label="Location" value={pairs(role!.campuses)} />
                <Fact label="Occupational level" value={role!.occupational_level} />
              </div>
            </div>

            {/* completeness + actions */}
            <div className="flex flex-wrap items-center gap-3">
              {completeness && (
                <div className="flex min-w-[220px] flex-1 items-center gap-2">
                  <Progress value={completeness.score} className="h-2 max-w-[160px]" />
                  <span className="text-sm text-gray-700">{completeness.score}% complete</span>
                  {completeness.neptune_ready
                    ? <Badge className="bg-emerald-50 text-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" />Ready for Neptune</Badge>
                    : <Badge className="bg-amber-50 text-amber-700" title={completeness.neptune_missing.join(', ')}><AlertTriangle className="mr-1 h-3 w-3" />Not ready for Neptune</Badge>}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {!editing && (
                  <Button size="sm" onClick={startEditing} disabled={!!busy}>
                    <Pencil className="mr-1 h-4 w-4" />{openV ? 'Edit draft' : approvedV ? 'Edit (new version)' : 'Write profile'}
                  </Button>
                )}
                {editing && (
                  <>
                    <Button size="sm" onClick={save} disabled={!!busy || !dirty}>
                      {busy === 'save' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Save draft
                    </Button>
                    <Button size="sm" variant="outline" onClick={stopEditing} disabled={!!busy}>Done editing</Button>
                  </>
                )}
                {openV && !editing && openV.status === 'draft' && (
                  <Button size="sm" variant="outline" onClick={() => act('submit', () => roleProfileService.submitDraft(roleId!), 'Sent for approval')} disabled={!!busy}>
                    <Send className="mr-1 h-4 w-4" />Submit for approval
                  </Button>
                )}
                {openV && !editing && bundle.can_approve && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setApproveOpen((v) => !v)} disabled={!!busy}>
                    <ShieldCheck className="mr-1 h-4 w-4" />Approve v{openV.version}
                  </Button>
                )}
                {!editing && sources.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setCopyOpen((v) => !v)} disabled={!!busy}>
                    <Copy className="mr-1 h-4 w-4" />Copy from another role
                  </Button>
                )}
                {(approvedV || openV) && (
                  <Button size="sm" variant="outline" onClick={() => download()} disabled={!!busy}>
                    {busy === 'download' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}Word
                  </Button>
                )}
                {openV && !editing && (
                  <Button size="sm" variant="ghost" className="text-red-600" disabled={!!busy}
                    onClick={() => { if (window.confirm(`Discard draft version ${openV.version}? The approved version is not affected.`)) act('discard', () => roleProfileService.discardDraft(roleId!), 'Draft discarded'); }}>
                    <Trash2 className="mr-1 h-4 w-4" />Discard draft
                  </Button>
                )}
              </div>
            </div>

            {approveOpen && openV && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-sm text-emerald-900">
                  Approving makes version {openV.version} the live profile for <b>{role!.title}</b>
                  {approvedV ? ` and retires version ${approvedV.version}` : ''}. Neptune requisitions and printed profiles will use it.
                </p>
                {openV.completeness.missing.length > 0 && (
                  <p className="mt-1 text-xs text-amber-800">Still incomplete: {openV.completeness.missing.join('; ')}.</p>
                )}
                <Textarea className="mt-2 bg-white" rows={2} value={approveNote} onChange={(e) => setApproveNote(e.target.value)} placeholder="Note (optional), e.g. what changed" />
                <div className="mt-2 flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!!busy}
                    onClick={async () => { const b = await act('approve', () => roleProfileService.approveDraft(roleId!, approveNote), `Version ${openV.version} approved`); if (b) { setApproveOpen(false); setApproveNote(''); setViewing('approved'); } }}>
                    {busy === 'approve' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1 h-4 w-4" />}Confirm approval
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setApproveOpen(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {copyOpen && (
              <div className="rounded-lg border bg-gray-50 p-3">
                <Label className="text-xs text-gray-600">Start this role's draft from another role's profile</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  <select className="h-9 min-w-[280px] flex-1 rounded-md border bg-white px-2 text-sm" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
                    <option value="">Choose a role…</option>
                    {sources.map((r) => <option key={r.role_id} value={r.role_id}>{r.title} ({r.role_code}){r.approved_version ? '' : ' - draft'}</option>)}
                  </select>
                  <Button size="sm" disabled={!copyFrom || !!busy}
                    onClick={async () => { const b = await act('copy', () => roleProfileService.copyFrom(roleId!, Number(copyFrom)), 'Draft copied'); if (b) { setCopyOpen(false); setCopyFrom(''); setViewing('open'); } }}>
                    Copy into a draft
                  </Button>
                </div>
                {openV && <p className="mt-1 text-xs text-amber-700">This replaces the content of the current draft (version {openV.version}).</p>}
              </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="profile"><FileText className="mr-1 h-4 w-4" />Profile</TabsTrigger>
                <TabsTrigger value="neptune">Neptune preview</TabsTrigger>
                <TabsTrigger value="history"><History className="mr-1 h-4 w-4" />History ({bundle.history.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="space-y-3">
                {!editing && approvedV && openV && (
                  <div className="flex gap-1 text-sm">
                    <Button size="sm" variant={viewing === 'approved' ? 'default' : 'outline'} onClick={() => setViewing('approved')}>Approved v{approvedV.version}</Button>
                    <Button size="sm" variant={viewing === 'open' ? 'default' : 'outline'} onClick={() => setViewing('open')}>{STATUS_BADGE[openV.status].label} v{openV.version}</Button>
                  </div>
                )}
                {!editing && shown && shown.source !== 'manual' && shown.source_filename && (
                  <p className="text-xs text-gray-500">Version {shown.version} came from a {SOURCE_LABEL[shown.source]}: {shown.source_filename}</p>
                )}
                {!editing && shown && <ProfileView version={shown} fw={fw} />}
                {!editing && !shown && (
                  <div className="rounded-lg border border-dashed p-8 text-center text-sm text-gray-500">
                    This role has no profile yet. Write one, copy one from a similar role, or upload the Word or Excel profile on the Profiles tab.
                  </div>
                )}
                {editing && form && (
                  <div className="space-y-4">
                    <Section title="Purpose of the role" subtitle="How it supports the achievement of Eduvos's overall performance objectives">
                      <Textarea rows={3} value={form.purpose} onChange={(e) => setF({ purpose: e.target.value })} />
                      <div>
                        <Label className="text-xs text-gray-600">Advert summary (optional)</Label>
                        <p className="text-[11px] text-gray-500">Only if the public advert should read differently from the purpose. Neptune uses the purpose otherwise.</p>
                        <Textarea rows={2} value={form.advert_summary} onChange={(e) => setF({ advert_summary: e.target.value })} />
                      </div>
                    </Section>
                    <Section title="Operating and financial results" subtitle="Outputs and measurement criteria">
                      {fw.kinds.map((k) => (
                        <OutRowsEditor key={k.code} label={k.label} help={k.help}
                          rows={k.code === 'accountability' ? form.accountability : form.deliverables}
                          onChange={(rows) => setF(k.code === 'accountability' ? { accountability: rows } : { deliverables: rows })} />
                      ))}
                    </Section>
                    {[
                      { code: 'internal-business-processes', title: 'Internal business processes and enablement', subtitle: 'Managerial work: fill in how the role contributes, or mark not applicable' },
                      { code: 'culture-people', title: 'Culture and people', subtitle: 'Leadership work' },
                      { code: 'customer-stakeholder', title: 'Customer and stakeholder relationships', subtitle: 'Who the role works with, and how' },
                    ].map((s) => (
                      <Section key={s.code} title={s.title} subtitle={s.subtitle}>
                        <FrameworkEditor defs={fw.framework_items.filter((f) => f.perspective === s.code)} rows={form.framework}
                          onChange={(code, row) => setF({ framework: { ...form.framework, [code]: row } })} />
                      </Section>
                    ))}
                    <Section title="Role requirements" subtitle="Minimum requirements screen applicants in or out; advantageous ones only rank them">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <Label className="text-xs font-semibold text-gray-700">Minimum education level</Label>
                          <select className="mt-1 h-9 w-full max-w-md rounded-md border bg-white px-2 text-sm" value={form.min_education_nqf}
                            onChange={(e) => setF({ min_education_nqf: e.target.value })}>
                            <option value="">Not set</option>
                            {fw.nqf_levels.map((n) => <option key={n.level} value={n.level}>{n.label}</option>)}
                          </select>
                        </div>
                        <ListBox label="Qualifications: minimum" value={form.qualifications_minimum} onChange={(v) => setF({ qualifications_minimum: v })} />
                        <ListBox label="Qualifications: advantageous" value={form.qualifications_advantageous} onChange={(v) => setF({ qualifications_advantageous: v })} />
                        <ListBox label="Experience: minimum" value={form.experience_minimum} onChange={(v) => setF({ experience_minimum: v })} />
                        <ListBox label="Experience: advantageous" value={form.experience_advantageous} onChange={(v) => setF({ experience_advantageous: v })} />
                        <ListBox label="Functional competencies" value={form.functional_competencies} onChange={(v) => setF({ functional_competencies: v })} rows={4} />
                        <div>
                          <ListBox label="Behavioural competencies" value={form.behavioural_competencies} onChange={(v) => setF({ behavioural_competencies: v })} rows={4} />
                          <Button type="button" size="sm" variant="link" className="h-auto px-0 text-xs"
                            onClick={() => setF({ behavioural_competencies: fw.default_behavioural_competencies.join('\n') })}>
                            Use the template's standard list
                          </Button>
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-gray-700">Unique work conditions</Label>
                          <Input value={form.work_conditions} onChange={(e) => setF({ work_conditions: e.target.value })} placeholder="e.g. Normal office environment" />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-gray-700">Date last reviewed</Label>
                          <Input type="date" value={form.reviewed_at} onChange={(e) => setF({ reviewed_at: e.target.value })} />
                          <p className="text-[11px] text-gray-500">Set automatically when a version is approved.</p>
                        </div>
                      </div>
                    </Section>
                    <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-white py-2">
                      <Button size="sm" onClick={save} disabled={!!busy || !dirty}>Save draft</Button>
                      <Button size="sm" variant="outline" onClick={stopEditing} disabled={!!busy}>Done editing</Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="neptune">
                {(() => {
                  const v = approvedV ?? openV;
                  if (!v) return <p className="text-sm text-gray-500">No profile yet.</p>;
                  const n = v.neptune;
                  return (
                    <div className="space-y-3">
                      <p className={cn('rounded-md p-2 text-xs', approvedV ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800')}>
                        {approvedV
                          ? `What a Neptune requisition for this role takes from approved version ${approvedV.version}. Duties and minimum requirements go across word for word; Neptune's AI may redraft the advert summary.`
                          : 'Preview from the draft. Neptune only ever receives an APPROVED version.'}
                      </p>
                      {[
                        ['Job advert summary', n.job_advert_summary],
                        ['Duties and responsibilities', n.duties_and_responsibilities],
                        ['Minimum requirements', n.minimum_requirements],
                        ['Advantageous', n.advantageous_requirements],
                        ['Minimum education level', n.minimum_education_label],
                        ['Functional competencies', n.functional_competencies.join(', ')],
                        ['Behavioural competencies', n.behavioural_competencies.join(', ')],
                      ].map(([label, value]) => (
                        <div key={label as string}>
                          <Label className="text-xs text-gray-500">{label}</Label>
                          <pre className="whitespace-pre-wrap rounded border bg-gray-50 p-2 font-sans text-sm">{value || '—'}</pre>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </TabsContent>

              <TabsContent value="history">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-gray-500"><th className="pb-1">Version</th><th>Status</th><th>Came from</th><th>Updated</th><th>Approved</th><th /></tr></thead>
                  <tbody>{bundle.history.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td className="py-1.5 font-mono">v{h.version}</td>
                      <td><Badge className={STATUS_BADGE[h.status].cls}>{STATUS_BADGE[h.status].label}</Badge></td>
                      <td className="text-gray-600">{SOURCE_LABEL[h.source]}{h.source_filename ? `: ${h.source_filename}` : ''}</td>
                      <td className="text-gray-600">{fmtDate(h.updated_at)}</td>
                      <td className="text-gray-600">{h.approved_at ? `${fmtDate(h.approved_at)}${h.approved_by_name ? ` by ${h.approved_by_name}` : ''}` : ''}</td>
                      <td className="text-right"><Button size="sm" variant="ghost" onClick={() => download(h.version)} disabled={!!busy}><Download className="h-4 w-4" /></Button></td>
                    </tr>
                  ))}</tbody>
                </table>
                {bundle.history.length === 0 && <p className="text-sm text-gray-500">No versions yet.</p>}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RoleProfileDialog;
